/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { forwardRef } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import {
  fontSizes,
  fontWeights,
  lineHeights,
  spacing,
} from "../../../../../global/emotion";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { WithAs } from "../../../../types/withAs.types";
import type { WithAsChild } from "../../../../types/withAsChild.types";
import { consoleWarnPackagesUi } from "../../../../utils/consoleWarnPackagesUi";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";

const Element: HallowElement = "span";

const variantStyles: {
  [key in `${NonNullable<TextProps["type"]>}${Uppercase<NonNullable<TextProps["size"]>>}`]?: Interpolation<ThemeType>;
} = {
  bodyS: (theme) => ({
    fontFamily: theme.fontFamilies.sansSerif,
    fontSize: fontSizes.bodyS,
    fontWeight: fontWeights.bodyS,
    lineHeight: lineHeights.bodyS,
  }),
  bodyM: (theme) => ({
    fontFamily: theme.fontFamilies.sansSerif,
    fontSize: fontSizes.bodyM,
    fontWeight: fontWeights.bodyM,
    lineHeight: lineHeights.bodyM,
  }),
  bodyL: (theme) => ({
    fontFamily: theme.fontFamilies.sansSerif,
    fontSize: fontSizes.bodyL,
    fontWeight: fontWeights.bodyL,
    lineHeight: lineHeights.bodyL,
  }),
  bodyXL: (theme) => ({
    fontFamily: theme.fontFamilies.sansSerif,
    fontSize: fontSizes.bodyXL,
    fontWeight: fontWeights.bodyXL,
    lineHeight: lineHeights.bodyXL,
  }),
  buttonXS: (theme) => ({
    fontFamily: theme.fontFamilies.sansSerif,
    fontSize: fontSizes.buttonXS,
    fontWeight: fontWeights.buttonXS,
    lineHeight: lineHeights.buttonXS,
  }),
  buttonS: (theme) => ({
    fontFamily: theme.fontFamilies.sansSerif,
    fontSize: fontSizes.buttonS,
    fontWeight: fontWeights.buttonS,
    lineHeight: lineHeights.buttonS,
  }),
  buttonM: (theme) => ({
    fontFamily: theme.fontFamilies.sansSerif,
    fontSize: fontSizes.buttonM,
    fontWeight: fontWeights.buttonM,
    lineHeight: lineHeights.buttonM,
  }),
  buttonL: (theme) => ({
    fontFamily: theme.fontFamilies.sansSerif,
    fontSize: fontSizes.buttonL,
    fontWeight: fontWeights.buttonL,
    lineHeight: lineHeights.buttonL,
  }),
  detailS: (theme) => ({
    fontFamily: theme.fontFamilies.sansSerif,
    fontSize: fontSizes.detailS,
    fontWeight: fontWeights.detailS,
    lineHeight: lineHeights.detailS,
  }),
  detailM: (theme) => ({
    fontFamily: theme.fontFamilies.sansSerif,
    fontSize: fontSizes.detailM,
    fontWeight: fontWeights.detailM,
    lineHeight: lineHeights.detailM,
  }),
  detailL: (theme) => ({
    fontFamily: theme.fontFamilies.sansSerif,
    fontSize: fontSizes.detailL,
    fontWeight: fontWeights.detailL,
    lineHeight: lineHeights.detailL,
  }),
  detailXL: (theme) => ({
    fontFamily: theme.fontFamilies.sansSerif,
    fontSize: fontSizes.detailXL,
    fontWeight: fontWeights.detailXL,
    lineHeight: lineHeights.detailXL,
  }),
  displayS: (theme) => ({
    fontFamily: theme.fontFamilies.sansSerif,
    fontSize: fontSizes.displayS,
    fontWeight: fontWeights.displayS,
    lineHeight: lineHeights.displayS,
  }),
  displayM: (theme) => ({
    fontFamily: theme.fontFamilies.sansSerif,
    fontSize: fontSizes.displayM,
    fontWeight: fontWeights.displayM,
    lineHeight: lineHeights.displayM,
  }),
  displayL: (theme) => ({
    fontFamily: theme.fontFamilies.sansSerif,
    fontSize: fontSizes.displayL,
    fontWeight: fontWeights.displayL,
    lineHeight: lineHeights.displayL,
  }),
  headlineS: (theme) => ({
    fontFamily: theme.fontFamilies.sansSerif,
    fontSize: fontSizes.headlineS,
    fontWeight: fontWeights.headlineS,
    lineHeight: lineHeights.headlineS,
  }),
  headlineM: (theme) => ({
    fontFamily: theme.fontFamilies.sansSerif,
    fontSize: fontSizes.headlineM,
    fontWeight: fontWeights.headlineM,
    lineHeight: lineHeights.headlineM,
  }),
  headlineL: (theme) => ({
    fontFamily: theme.fontFamilies.sansSerif,
    fontSize: fontSizes.headlineL,
    fontWeight: fontWeights.headlineL,
    lineHeight: lineHeights.headlineL,
  }),
  titleXS: (theme) => ({
    fontFamily: theme.fontFamilies.sansSerif,
    fontSize: fontSizes.titleXS,
    fontWeight: fontWeights.titleXS,
    lineHeight: lineHeights.titleXS,
  }),
  titleS: (theme) => ({
    fontFamily: theme.fontFamilies.sansSerif,
    fontSize: fontSizes.titleS,
    fontWeight: fontWeights.titleS,
    lineHeight: lineHeights.titleS,
  }),
  titleM: (theme) => ({
    fontFamily: theme.fontFamilies.sansSerif,
    fontSize: fontSizes.titleM,
    fontWeight: fontWeights.titleM,
    lineHeight: lineHeights.titleM,
  }),
  titleL: (theme) => ({
    fontFamily: theme.fontFamilies.sansSerif,
    fontSize: fontSizes.titleL,
    fontWeight: fontWeights.titleL,
    lineHeight: lineHeights.titleL,
  }),
  titleXL: (theme) => ({
    fontFamily: theme.fontFamilies.sansSerif,
    fontSize: fontSizes.titleXL,
    fontWeight: fontWeights.titleXL,
    lineHeight: lineHeights.titleXL,
  }),
  quoteM: (theme) => ({
    fontFamily: theme.fontFamilies.serif,
    fontSize: fontSizes.quoteM,
    fontWeight: fontWeights.quoteM,
    lineHeight: lineHeights.quoteM,
  }),
  quoteL: (theme) => ({
    fontFamily: theme.fontFamilies.serif,
    fontSize: fontSizes.quoteL,
    fontWeight: fontWeights.quoteL,
    lineHeight: lineHeights.quoteL,
  }),
  quoteXL: (theme) => ({
    fontFamily: theme.fontFamilies.serif,
    fontSize: fontSizes.quoteXL,
    fontWeight: fontWeights.quoteXL,
    lineHeight: lineHeights.quoteXL,
  }),
  bibleM: (theme) => ({
    fontFamily: theme.fontFamilies.serif,
    fontSize: theme.fontSizes.bodyBible,
    fontWeight: theme.fontWeights.bodyBible,
    lineHeight: theme.lineHeights.bodyBible,
  }),
};

const baseStyle: Interpolation = {
  overflowWrap: "break-word",
};
const lineClampStyle = (lineClamp: number): Interpolation => ({
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: lineClamp,
  display: "-webkit-box",
  minWidth: 0,
  overflow: "hidden",
});
const overflowEllipsisStyle: Interpolation = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  width: spacing.full,
};

export type TextProps = WithAsChild<
  WithAs<
    HallowElementProps<typeof Element>,
    | "div"
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "li"
    | "p"
    | "span"
    | "blockquote"
  >
> &
  (
    | {
        size?: "xs" | "s" | "m" | "l" | "xl";
        type?: "title";
      }
    | {
        size?: "xs" | "s" | "m" | "l";
        type?: "button";
      }
    | {
        size?: "s" | "m" | "l" | "xl";
        type?: "body" | "detail";
      }
    | {
        size?: "s" | "m" | "l";
        type?: "display" | "headline" | "title";
      }
    | {
        size?: "m" | "l" | "xl";
        type?: "quote";
      }
    | { size?: "m"; type?: "bible" }
  ) & {
    lineClamp?: number;
    overflow?: "ellipsis";
  };

export const Text = forwardRef<any, TextProps>(
  (
    {
      as = Element,
      asChild = false,
      children,
      lineClamp,
      overflow,
      size = "m",
      type = "body",
      title,
      ...props
    },
    ref,
  ) => {
    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement: as,
    });

    const determinedVariantStyle =
      variantStyles[
        `${type}${size.toUpperCase()}` as keyof typeof variantStyles
      ];
    if (!determinedVariantStyle) {
      consoleWarnPackagesUi({
        heading: "Text.tsx component",
        warning: `No style for size "${size}" and type "${type}" exists.`,
      });
    }

    return (
      <DeterminedElement
        ref={ref}
        css={[
          baseStyle,
          determinedVariantStyle,
          lineClamp && lineClampStyle(lineClamp),
          overflow === "ellipsis" && overflowEllipsisStyle,
        ]}
        {...props}
        title={title ?? (typeof children === "string" ? children : undefined)}
      >
        {children}
      </DeterminedElement>
    );
  },
);

Text.displayName = "Text";
